import React, { useMemo } from "react";
import { Select as NovusSelect } from "@arcadis-intelligence/novus-design-system";
import { SelectProps } from "./helpers";
import { useTranslation } from "react-i18next";
import { selectStyles } from "../variants";
import styled from "styled-components";

export const SelectComponent: React.FC<SelectProps> = ({
    options = [],
    value,
    asterisk = false,
    bgColor,
    components,
    defaultValue,
    isDisabled = false,
    isClearable,
    isMulti = false,
    isSearchable = false,
    menuPlacement = "auto",
    menuPortalTarget = document.getElementById("root"),
    onChange,
    onMenuOpen,
    placeholder = "Select Options",
    placeholderColor,
    onMenuClose,
    isDuplicated = false,
    isOptionDisabled,
    isOpened,
    isMultiSelectWithTags,
    displayName,
    ...props
}) => {
    const { t } = useTranslation();

    let updatedOptions: { label: string; value: number }[] = [];

    if (isDuplicated) {
        const data = new Map();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options?.forEach((i: any) => {
            data.set(i.value, i.label);
        });
        updatedOptions = Array.from(data, ([key, value]) => ({
            label: t(value),
            value: key
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function translateOptions(options: any) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return options.map((option: any) => {
            if (option.label && typeof option.label === "string") {
                return {
                    ...option,
                    label: t(`${option.label}`)
                };
            }

            return option;
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function translateValue(value: any) {
        if (value && value.label) {
            return {
                ...value,
                label: t(`${value.label}`)
            };
        }

        return value;
    }
    const translatedOptions = useMemo(() => translateOptions(options), [options]);

    const translatedValue = useMemo(
        () => translateValue(value ?? defaultValue),
        [value, defaultValue]
    );

    return (
        <>
            <NovusSelect
                displayName={t(displayName)}
                className="bg-white border-radius-8"
                options={isDuplicated ? updatedOptions : translatedOptions}
                value={translateValue(value)}
                onChange={onChange}
                isMultiSelect={isMulti}
                placeHolder={t(`${placeholder}`)}
                isSearchable={isSearchable}
                withCheckbox={isMulti}
                components={components}
                bgColor={bgColor}
                isClearable={isClearable}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                disabled={isDisabled}
                menuPortalTarget={menuPortalTarget}
                defaultValue={translatedValue}
                isScrollActive={true}
                showIcon={false}
                menuPlacement={menuPlacement}
                isMultiSelectWithTags={isMultiSelectWithTags}
                filterOption={(option: any, searchText: any) => {
                    if (typeof option.label !== typeof "") {
                        return true;
                    }

                    return (
                        option.label.toLowerCase().includes(searchText.toLowerCase()) &&
                        option.label !== "hidden"
                    );
                }}
                isDisabledOption={isOptionDisabled}
                asterisk={asterisk}
                isOpened={true}
                {...selectStyles}
                {...props}
            />
        </>
    );
};
